import { type ComponentPropsWithoutRef, type ElementType } from 'react'
import clsx from 'clsx'
import { text, type TextVariants } from './Text.css'

type TextElement = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'div'

type TextProps<T extends TextElement = 'p'> = {
  /** The HTML element to render */
  as?: T
  /** Additional CSS class names */
  className?: string
} & TextVariants &
  ComponentPropsWithoutRef<T>

/**
 * Map variant to default HTML element.
 * When variant is set but `as` is not, use this element.
 */
const variantToElement: Record<string, TextElement> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  label: 'label',
  bodyLg: 'p',
  body: 'p',
  bodySm: 'p',
  bodyXs: 'p',
  caption: 'span',
}

/**
 * A polymorphic text component that can render as any text element.
 *
 * @example
 * ```tsx
 * // Using variant (recommended for design system consistency)
 * <Text variant="h1">Page Title</Text>
 * <Text variant="body">Paragraph text</Text>
 * <Text variant="h1" as="h2">Visually h1, semantically h2</Text>
 *
 * // Using size/weight directly (for custom styles)
 * <Text size="4xl" weight="bold">Custom heading</Text>
 * <Text as="span" color="muted" size="sm">Helper text</Text>
 * ```
 */
export function Text<T extends TextElement = 'p'>({
  as,
  variant,
  size,
  weight,
  color,
  align,
  truncate,
  className,
  ...props
}: TextProps<T>) {
  // Determine the element: explicit `as` > variant default > 'p'
  const defaultElement = variant ? variantToElement[variant] ?? 'p' : 'p'
  const Component = (as ?? defaultElement) as ElementType

  return (
    <Component
      className={clsx(text({ variant, size, weight, color, align, truncate }), className)}
      {...props}
    />
  )
}
